import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign,
  Search,
  Download,
  CreditCard,
  Banknote,
  TrendingUp,
  Calendar,
  User,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  AlertTriangle,
  Building
} from 'lucide-react';
import { ComposedChart, Bar, Line, Area, AreaChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useSearchParams } from 'react-router-dom';
import apiService from './services/api';
import { Panel, StatCard, Button, Badge, LoadingSpinner, Section } from './components';

function FinancePage() {
  const [searchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showUnmatchedOnly, setShowUnmatchedOnly] = useState(searchParams.get('unmatched') === 'true');
  const [sortField, setSortField] = useState('date_time');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, tips: 0, fees: 0, net_revenue: 0, count: 0, square_count: 0, cash_count: 0 });
  const itemsPerPage = 50;

  // Stripe state
  const [stripeData, setStripeData] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [refunding, setRefunding] = useState(null);
  const [refundForm, setRefundForm] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundError, setRefundError] = useState('');

  // P&L state
  const [activeTab, setActiveTab] = useState('pnl');
  const [pnlData, setPnlData] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [pnlYear, setPnlYear] = useState(2026);
  const [mappings, setMappings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showMappings, setShowMappings] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Payouts state
  const [payoutsData, setPayoutsData] = useState(null);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutsYear, setPayoutsYear] = useState(2026);
  const [payoutsSourceFilter, setPayoutsSourceFilter] = useState('all');

  // Square payout mapping (payment_id -> payout_id)
  const [squarePayoutMap, setSquarePayoutMap] = useState({});

  // Treasury state
  const [treasury, setTreasury] = useState(null);

  // Fetch stats and Square payout map once
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await apiService.getTransactionStats({ year: 2026 });
        setStats(data);
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    };
    const fetchSquarePayoutMap = async () => {
      try {
        const data = await apiService.get('/amn/metrics/square-payout-map/', { year: 2026 });
        setSquarePayoutMap(data.mapping || {});
      } catch (err) {
        console.error('Error fetching square payout map:', err);
      }
    };
    fetchStats();
    fetchSquarePayoutMap();
    const fetchTreasury = async () => {
      try {
        const data = await apiService.get('/amn/metrics/treasury/');
        setTreasury(data);
      } catch (err) {
        console.error('Error fetching treasury:', err);
      }
    };
    fetchTreasury();
  }, []);

  // Fetch transactions (server-side paginated)
  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        year: 2026,
        ordering: `${sortDirection === 'desc' ? '-' : ''}${sortField}`,
        page: currentPage,
        page_size: itemsPerPage,
      };

      if (searchTerm) params.search = searchTerm;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (showUnmatchedOnly) params.appointment__isnull = 'True';

      const response = await apiService.getTransactions(params);
      setTransactions(response.results || response || []);
      setTotalCount(response.count || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortField, sortDirection, searchTerm, sourceFilter, statusFilter, showUnmatchedOnly]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Fetch P&L data
  const fetchPnl = async () => {
    setPnlLoading(true);
    try {
      const data = await apiService.getPnl({ year: pnlYear });
      setPnlData(data);
    } catch (err) {
      console.error('Error fetching P&L:', err);
    } finally {
      setPnlLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'pnl') fetchPnl();
  }, [activeTab, pnlYear]);

  const fetchMappings = async () => {
    try {
      const data = await apiService.getPnlMappings();
      setMappings(data.mappings || []);
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching mappings:', err);
    }
  };

  useEffect(() => {
    if (showMappings) fetchMappings();
  }, [showMappings]);

  const fetchPayouts = async (year) => {
    setPayoutsLoading(true);
    try {
      const data = await apiService.get('/amn/metrics/payouts/', { year });
      setPayoutsData(data);
    } catch (err) {
      console.error('Error fetching payouts:', err);
    } finally {
      setPayoutsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'payouts') fetchPayouts(payoutsYear);
  }, [activeTab, payoutsYear]);

  const handleCategoryChange = async (mappingId, newCategory) => {
    try {
      await apiService.updatePnlMapping(mappingId, { category: newCategory });
      setMappings(prev => prev.map(m => m.id === mappingId ? { ...m, category: newCategory } : m));
      fetchPnl();
    } catch (err) {
      console.error('Error updating mapping:', err);
    }
  };

  // Debounce search
  const [searchTimeout, setSearchTimeout] = useState(null);
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setCurrentPage(1);
    }, 400));
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'square':
        return <CreditCard className="w-3 h-3 text-blue-600" />;
      case 'cash':
        return <Banknote className="w-3 h-3 text-green-600" />;
      default:
        return <DollarSign className="w-3 h-3 text-gray-600" />;
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  const exportCSV = async () => {
    // Fetch all pages for export
    let allTransactions = [];
    let page = 1;
    let hasMore = true;
    while (hasMore) {
      const params = { year: 2026, ordering: '-date_time', page, page_size: 200 };
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await apiService.getTransactions(params);
      const results = response.results || response || [];
      allTransactions = [...allTransactions, ...results];
      hasMore = !!response.next;
      page++;
      if (page > 100) break;
    }

    const headers = ['Date', 'Client', 'Amount', 'Tip', 'Fee', 'Net', 'Source', 'Status', 'Square ID'];
    const rows = allTransactions.map(t => [
      formatDate(t.date_time),
      t.client_name || 'N/A',
      t.amount,
      t.tip || 0,
      t.fee || 0,
      (parseFloat(t.amount || 0) - parseFloat(t.fee || 0)).toFixed(2),
      t.source,
      t.status,
      t.square_id || ''
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_2026_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Page Header */}
        <div className="mb-3">
          <h1 className="text-lg font-semibold text-gray-900 leading-none mb-2">Finance</h1>
          {treasury && (
            <Panel className="!p-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {treasury.accounts.map(a => (
                    <div key={a.name} className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2.5 py-1.5">
                      <Building className="w-3 h-3 text-green-600 flex-shrink-0" />
                      <span className="text-xs text-gray-500">{a.name.replace('Mercury ', '')}</span>
                      <span className="text-xs font-semibold text-gray-900">${a.balance.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 bg-green-50 rounded-lg px-3 py-1.5">
                  <DollarSign className="w-3.5 h-3.5 text-green-700" />
                  <span className="text-sm text-green-700 font-bold">Treasury: ${treasury.total.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              </div>
              {treasury.history && treasury.history.length > 1 && (
                <ResponsiveContainer width="100%" height={120}>
                  <AreaChart data={treasury.history}>
                    <defs>
                      <linearGradient id="treasuryGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#16a34a" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={9} tickLine={false} axisLine={false}
                      tickFormatter={d => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      interval="preserveStartEnd" minTickGap={40} />
                    <YAxis stroke="#9ca3af" fontSize={9} tickLine={false} axisLine={false} width={50}
                      tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={v => [`$${v.toLocaleString(undefined, {minimumFractionDigits: 2})}`, 'Balance']}
                      labelFormatter={d => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #e5e7eb' }} />
                    <Area type="monotone" dataKey="balance" stroke="#16a34a" strokeWidth={2} fill="url(#treasuryGrad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Panel>
          )}
        </div>

        {/* Tab Navigation — responsive: wraps on mobile, icons only on small screens */}
        <div className="grid grid-cols-4 gap-1 mb-3 bg-white rounded-lg p-1 shadow-sm">
          <button
            onClick={() => setActiveTab('pnl')}
            className={`flex items-center justify-center gap-1.5 px-2 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
              activeTab === 'pnl' ? 'bg-red-800 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <TrendingUp className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline">P&L</span>
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`flex items-center justify-center gap-1.5 px-2 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
              activeTab === 'transactions' ? 'bg-red-800 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <CreditCard className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Square</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('stripe');
              if (!stripeData && !stripeLoading) {
                setStripeLoading(true);
                apiService.get('/amn/metrics/stripe/', { limit: 100 })
                  .then(data => setStripeData(data))
                  .catch(() => {})
                  .finally(() => setStripeLoading(false));
              }
            }}
            className={`flex items-center justify-center gap-1.5 px-2 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
              activeTab === 'stripe' ? 'bg-red-800 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <DollarSign className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Stripe</span>
          </button>
          <button
            onClick={() => setActiveTab('payouts')}
            className={`flex items-center justify-center gap-1.5 px-2 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
              activeTab === 'payouts' ? 'bg-red-800 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Building className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Payouts</span>
          </button>
        </div>

        {/* STRIPE TAB */}
        {activeTab === 'stripe' && (
          <>
            {stripeLoading ? (
              <div className="p-6"><LoadingSpinner /></div>
            ) : stripeData ? (() => {
              const charges = stripeData.transactions.filter(t => t.type !== 'payout');
              return (
              <>
                <div className="mb-3 flex justify-between items-center">
                  <p className="text-xs text-gray-600">{charges.length} Stripe transactions for 2026</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
                  <StatCard value={`$${stripeData.summary.total_charges.toLocaleString(undefined, {minimumFractionDigits: 2})}`} label="Total Revenue" icon={DollarSign} />
                  <StatCard value={`$${stripeData.summary.total_net.toLocaleString(undefined, {minimumFractionDigits: 2})}`} label="Net (After Fees)" icon={TrendingUp} />
                  <StatCard value={`$${stripeData.summary.total_fees.toLocaleString(undefined, {minimumFractionDigits: 2})}`} label="Fees Paid" icon={CreditCard} />
                  <StatCard value={stripeData.summary.charge_count} label="Transactions" icon={Calendar} />
                </div>

                {/* Transactions Table */}
                <Panel noPadding className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Date</th>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Client</th>
                          <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</th>
                          <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Fee</th>
                          <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Net</th>
                          <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Type</th>
                          <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Action</th>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Payout</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {charges.map(t => (
                          <tr key={t.id} className={`hover:bg-gray-50 ${t.type === 'refund' ? 'bg-red-50/30' : ''}`}>
                            <td className="px-2 py-1.5 text-xs text-gray-900 whitespace-nowrap">
                              {new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </td>
                            <td className="px-2 py-1.5">
                              {t.client_name ? (
                                <div className="flex items-center gap-1">
                                  <User className="w-3 h-3 text-red-700" />
                                  <span className="text-xs font-medium text-red-700">{t.client_name}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">{t.description || '-'}</span>
                              )}
                            </td>
                            <td className={`px-2 py-1.5 text-xs text-right font-medium ${t.type === 'refund' ? 'text-red-600' : 'text-gray-900'}`}>
                              {t.type === 'refund' ? '-' : ''}${Math.abs(t.amount).toFixed(2)}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-right text-red-600">
                              {t.fee > 0 ? `$${t.fee.toFixed(2)}` : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-right font-medium text-gray-900">
                              {t.net < 0 ? '-' : ''}${Math.abs(t.net).toFixed(2)}
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                t.type === 'charge' ? 'bg-green-100 text-green-800'
                                : t.type === 'refund' ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-600'
                              }`}>{t.type === 'charge' ? 'Completed' : t.type === 'refund' ? 'Refunded' : t.type}</span>
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              {t.type === 'charge' && t.source && (
                                refundForm === t.id ? (
                                  <div className="flex flex-col gap-1 min-w-[140px]">
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-gray-500">$</span>
                                      <input type="number" step="0.01" min="0.01" max={t.amount}
                                        value={refundAmount} onChange={e => { setRefundAmount(e.target.value); setRefundError(''); }}
                                        placeholder={t.amount.toFixed(2)}
                                        className="w-20 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-red-800" autoFocus />
                                    </div>
                                    {refundError && <span className="text-[10px] text-red-600">{refundError}</span>}
                                    <div className="flex gap-1">
                                      <button onClick={async () => {
                                        const amt = refundAmount ? parseFloat(refundAmount) : t.amount;
                                        if (isNaN(amt) || amt <= 0 || amt > t.amount) { setRefundError(`Max $${t.amount.toFixed(2)}`); return; }
                                        setRefunding(t.id);
                                        try {
                                          const payload = { charge_id: t.source };
                                          if (amt < t.amount) payload.amount = amt;
                                          await apiService.post('/amn/metrics/stripe/', payload);
                                          setRefundForm(null); setRefundAmount('');
                                          setStripeData(null); setStripeLoading(true);
                                          const data = await apiService.get('/amn/metrics/stripe/', { year: 2026 });
                                          setStripeData(data);
                                        } catch (err) { setRefundError(err.message || 'Failed'); }
                                        finally { setRefunding(null); setStripeLoading(false); }
                                      }} disabled={refunding === t.id}
                                        className="text-[10px] px-2 py-0.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                                        {refunding === t.id ? '...' : 'Confirm'}
                                      </button>
                                      <button onClick={() => { setRefundForm(null); setRefundAmount(''); setRefundError(''); }}
                                        className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200">Cancel</button>
                                    </div>
                                  </div>
                                ) : (
                                  <button onClick={() => { setRefundForm(t.id); setRefundAmount(t.amount.toFixed(2)); setRefundError(''); }}
                                    disabled={refunding === t.id}
                                    className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50">
                                    Refund
                                  </button>
                                )
                              )}
                            </td>
                            <td className="px-2 py-1.5">
                              {t.payout_id ? (
                                <span className="text-xs text-purple-600 font-mono" title={t.payout_id}>
                                  {t.payout_id.slice(-8)}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              </>
              );
            })() : null}
          </>
        )}

        {/* PAYOUTS TAB */}
        {activeTab === 'payouts' && (
          <>
            {payoutsLoading ? (
              <div className="p-6"><LoadingSpinner /></div>
            ) : payoutsData ? (() => {
              const payouts = payoutsSourceFilter === 'all'
                ? payoutsData.payouts.filter(p => !p.error)
                : payoutsData.payouts.filter(p => !p.error && p.source === payoutsSourceFilter);
              const summary = payoutsData.summary;

              // Build payout_id -> transaction count
              const payoutTxnCounts = {};
              Object.values(squarePayoutMap).forEach(poId => {
                payoutTxnCounts[poId] = (payoutTxnCounts[poId] || 0) + 1;
              });
              if (stripeData) {
                stripeData.transactions.filter(t => t.payout_id && t.type !== 'payout').forEach(t => {
                  payoutTxnCounts[t.payout_id] = (payoutTxnCounts[t.payout_id] || 0) + 1;
                });
              }
              return (
              <>
                <div className="mb-3 flex justify-between items-center">
                  <p className="text-xs text-gray-600">{summary.total_count} payouts for {payoutsYear}</p>
                  <div className="flex items-center gap-2">
                    <select
                      value={payoutsSourceFilter}
                      onChange={(e) => setPayoutsSourceFilter(e.target.value)}
                      className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                    >
                      <option value="all">All Sources</option>
                      <option value="square">Square</option>
                      <option value="stripe">Stripe</option>
                    </select>
                    <select
                      value={payoutsYear}
                      onChange={(e) => setPayoutsYear(parseInt(e.target.value))}
                      className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                    >
                      <option value={2026}>2026</option>
                      <option value={2025}>2025</option>
                    </select>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
                  <StatCard value={`$${summary.combined_total.toLocaleString(undefined, {minimumFractionDigits: 2})}`} label="Total Payouts" icon={Building} />
                  <StatCard value={`$${summary.square_total.toLocaleString(undefined, {minimumFractionDigits: 2})}`} label="Square Payouts" icon={CreditCard} />
                  <StatCard value={`$${summary.stripe_total.toLocaleString(undefined, {minimumFractionDigits: 2})}`} label="Stripe Payouts" icon={DollarSign} />
                  <StatCard value={summary.square_count} label="Square Count" icon={Calendar} />
                  <StatCard value={summary.stripe_count} label="Stripe Count" icon={Calendar} />
                </div>

                {/* Payouts Table */}
                <Panel noPadding className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Payout ID</th>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Date</th>
                          <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Source</th>
                          <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</th>
                          <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Txns</th>
                          <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Arrival Date</th>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Description</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {payouts.map((p) => (
                          <tr key={p.id} className="hover:bg-gray-50">
                            <td className="px-2 py-1.5">
                              <span className="text-xs text-purple-600 font-mono" title={p.id}>
                                {p.id.slice(-8)}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-xs text-gray-900 whitespace-nowrap">
                              {p.date ? new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                p.source === 'square' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                              }`}>{p.source === 'square' ? 'Square' : 'Stripe'}</span>
                            </td>
                            <td className="px-2 py-1.5 text-xs text-right font-medium text-gray-900">
                              ${Math.abs(p.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </td>
                            <td className="px-2 py-1.5 text-center text-xs text-gray-700 font-medium">
                              {payoutTxnCounts[p.id] || '-'}
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <Badge variant={
                                ['paid', 'sent'].includes(p.status) ? 'success'
                                : p.status === 'in_transit' ? 'warning'
                                : p.status === 'failed' ? 'error'
                                : 'neutral'
                              } small>
                                {p.status}
                              </Badge>
                            </td>
                            <td className="px-2 py-1.5 text-xs text-gray-600 whitespace-nowrap">
                              {p.arrival_date ? new Date(p.arrival_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-gray-600 truncate max-w-[200px]">
                              {p.description || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>

                {payouts.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <Building className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-xs">No payouts found</p>
                  </div>
                )}
              </>
              );
            })() : null}
          </>
        )}

        {/* P&L TAB */}
        {activeTab === 'pnl' && (
          <>
            <Panel className="mb-3">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-700">Year</label>
                <select
                  value={pnlYear}
                  onChange={(e) => setPnlYear(parseInt(e.target.value))}
                  className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value={2026}>2026</option>
                  <option value={2025}>2025</option>
                </select>
                <button
                  disabled={syncing}
                  onClick={async () => {
                    setSyncing(true);
                    try {
                      await apiService.post('/amn/metrics/pnl/sync/');
                      setTimeout(async () => {
                        await fetchPnl();
                        setSyncing(false);
                      }, 8000);
                    } catch (err) {
                      alert('Sync failed: ' + err.message);
                      setSyncing(false);
                    }
                  }}
                  className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border border-gray-200 disabled:opacity-50"
                >
                  {syncing ? '⟳ Syncing...' : '⟳ Sync Mercury'}
                </button>
              </div>
            </Panel>

            {pnlLoading ? (
              <Panel className="p-6"><LoadingSpinner /></Panel>
            ) : pnlData ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  <Panel className="p-2">
                    <p className="text-xs text-gray-500">Total Revenue</p>
                    <p className="text-lg font-semibold text-green-700">${pnlData.summary?.total_revenue?.toLocaleString()}</p>
                  </Panel>
                  <Panel className="p-2">
                    <p className="text-xs text-gray-500">Operating Expenses</p>
                    <p className="text-lg font-semibold text-red-700">${((pnlData.summary?.total_expenses || 0) - (pnlData.summary?.expense_breakdown?.owner_draw || 0)).toLocaleString()}</p>
                  </Panel>
                  <Panel className="p-2 border-purple-200 bg-purple-50">
                    <p className="text-xs text-purple-600">SDE</p>
                    <p className="text-lg font-semibold text-purple-700">
                      ${((pnlData.summary?.net_profit || 0) + (pnlData.summary?.expense_breakdown?.owner_draw || 0)).toLocaleString()}
                    </p>
                  </Panel>
                  <Panel className="p-2">
                    <p className="text-xs text-gray-500">SDE Margin</p>
                    <p className={`text-lg font-semibold ${pnlData.summary?.net_profit >= 0 ? 'text-purple-700' : 'text-red-700'}`}>
                      {pnlData.summary?.total_revenue ? Math.round(((pnlData.summary?.net_profit || 0) + (pnlData.summary?.expense_breakdown?.owner_draw || 0)) / pnlData.summary.total_revenue * 100) : 0}%
                    </p>
                  </Panel>
                </div>

                {/* Expense Breakdown + Owner Draw */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[
                    { key: 'contractors', label: 'Contractors', color: 'text-red-700' },
                    { key: 'payroll', label: 'Payroll', color: 'text-orange-700' },
                    { key: 'marketing', label: 'Marketing', color: 'text-blue-700' },
                    { key: 'operating', label: 'Operating', color: 'text-amber-700' },
                  ].map(({ key, label, color }) => (
                    <Panel className="p-2" key={key}>
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className={`text-sm font-semibold ${color}`}>${(pnlData.summary?.expense_breakdown?.[key] || 0).toLocaleString()}</p>
                    </Panel>
                  ))}
                </div>
                {(pnlData.summary?.expense_breakdown?.owner_draw || 0) > 0 && (
                  <Panel className="mb-3 !p-2 bg-purple-50/50 border-purple-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-purple-600">Owner Distributions (not an operating expense)</span>
                      <span className="text-sm font-semibold text-purple-700">${(pnlData.summary?.expense_breakdown?.owner_draw || 0).toLocaleString()}</span>
                    </div>
                  </Panel>
                )}

                {/* YTD Cash Flow Reconciliation */}
                {pnlData.cash_flow && (
                  <Panel className="mb-3 !p-2 bg-gray-50 border-gray-200">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">YTD Cash Flow (excl. internal transfers)</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <p className="text-[10px] text-gray-400">Inflows</p>
                        <p className="text-sm font-semibold text-green-700">${pnlData.cash_flow.total_inflows?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400">Outflows</p>
                        <p className="text-sm font-semibold text-red-700">${Math.abs(pnlData.cash_flow.total_outflows || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400">Operating Cash Flow</p>
                        <p className={`text-sm font-semibold ${(pnlData.cash_flow.operating_cash_flow || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          ${(pnlData.cash_flow.operating_cash_flow || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </Panel>
                )}

                {/* Monthly Chart */}
                {pnlData.monthly?.length > 0 && (
                  <Panel className="mb-3">
                    <Section title="Monthly P&L" tight>
                      <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={pnlData.monthly.map(m => ({
                          month: new Date(m.month + '-01T12:00:00').toLocaleDateString('en-US', { month: 'short' }),
                          revenue: m.revenue,
                          contractors: m.expenses.contractors || 0,
                          payroll: m.expenses.payroll || 0,
                          marketing: m.expenses.marketing || 0,
                          operating: m.expenses.operating || 0,
                          sde: m.net_profit + (m.expenses.owner_draw || 0),
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                          <XAxis dataKey="month" stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} />
                          <YAxis stroke="#9ca3af" fontSize={9} tickLine={false} axisLine={false} width={45} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                          <Tooltip
                            formatter={(v, name) => [`$${v.toLocaleString()}`, {sde:'SDE',contractors:'Contractors',payroll:'Payroll',marketing:'Marketing',operating:'Operating',revenue:'Revenue'}[name] || name]}
                            contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                          />
                          <Bar dataKey="revenue" fill="#16a34a" radius={[3, 3, 0, 0]} name="revenue" />
                          <Bar dataKey="contractors" fill="#dc2626" name="contractors" />
                          <Bar dataKey="payroll" fill="#ea580c" name="payroll" />
                          <Bar dataKey="marketing" fill="#2563eb" name="marketing" />
                          <Bar dataKey="operating" fill="#f59e0b" radius={[3, 3, 0, 0]} name="operating" />
                          <Line type="monotone" dataKey="sde" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} name="sde" />
                        </ComposedChart>
                      </ResponsiveContainer>
                      <div className="flex items-center justify-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                        <div className="flex items-center gap-1"><div className="w-3 h-2 bg-green-600 rounded-sm" /><span>Revenue</span></div>
                        <div className="flex items-center gap-1"><div className="w-3 h-2 bg-red-600 rounded-sm" /><span>Contractors</span></div>
                        <div className="flex items-center gap-1"><div className="w-3 h-2 bg-orange-600 rounded-sm" /><span>Payroll</span></div>
                        <div className="flex items-center gap-1"><div className="w-3 h-2 bg-blue-600 rounded-sm" /><span>Marketing</span></div>
                        <div className="flex items-center gap-1"><div className="w-3 h-2 bg-amber-500 rounded-sm" /><span>Operating</span></div>
                        <div className="flex items-center gap-1"><div className="w-2 h-0.5 bg-purple-500" /><span>SDE</span></div>
                      </div>
                    </Section>
                  </Panel>
                )}

                {/* Monthly Table */}
                {pnlData.monthly?.length > 0 && (
                  <Panel noPadding className="mb-3">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                            <th className="px-2 py-1.5 text-right text-xs font-medium text-green-600 uppercase">Revenue</th>
                            <th className="px-2 py-1.5 text-right text-xs font-medium text-red-600 uppercase">Contractors</th>
                            <th className="px-2 py-1.5 text-right text-xs font-medium text-orange-600 uppercase">Payroll</th>
                            <th className="px-2 py-1.5 text-right text-xs font-medium text-blue-600 uppercase">Marketing</th>
                            <th className="px-2 py-1.5 text-right text-xs font-medium text-amber-600 uppercase">Operating</th>
                            <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Total Exp.</th>
                            <th className="px-2 py-1.5 text-right text-xs font-medium text-purple-600 uppercase">SDE</th>
                            <th className="px-2 py-1.5 text-right text-xs font-medium text-purple-400 uppercase">SDE %</th>
                            <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Net Cash</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {pnlData.monthly.map((m) => {
                            const sde = m.net_profit + (m.expenses.owner_draw || 0);
                            const sdePct = m.revenue > 0 ? Math.round((sde / m.revenue) * 100) : 0;
                            return (
                            <tr key={m.month} className="hover:bg-gray-50">
                              <td className="px-2 py-1.5 text-xs font-medium text-gray-900">
                                {new Date(m.month + '-01T12:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                              </td>
                              <td className="px-2 py-1.5 text-xs text-right text-green-700 font-medium">${m.revenue.toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-xs text-right text-red-600">${(m.expenses.contractors || 0).toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-xs text-right text-orange-600">${(m.expenses.payroll || 0).toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-xs text-right text-blue-600">${(m.expenses.marketing || 0).toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-xs text-right text-amber-600">${(m.expenses.operating || 0).toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-xs text-right font-medium text-gray-900">${(m.expenses.total - (m.expenses.owner_draw || 0)).toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-xs text-right font-semibold text-purple-700">
                                ${sde.toLocaleString()}
                              </td>
                              <td className={`px-2 py-1.5 text-xs text-right font-semibold ${sdePct >= 40 ? 'text-green-600' : sdePct >= 25 ? 'text-purple-600' : 'text-red-600'}`}>
                                {sdePct}%
                              </td>
                              <td className={`px-2 py-1.5 text-xs text-right font-semibold ${(m.net_cash_flow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                ${(m.net_cash_flow || 0).toLocaleString()}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-gray-50 font-semibold">
                          <tr>
                            <td className="px-2 py-1.5 text-xs text-gray-900">Total</td>
                            <td className="px-2 py-1.5 text-xs text-right text-green-700">${pnlData.summary?.total_revenue?.toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-xs text-right text-red-600">${(pnlData.summary?.expense_breakdown?.contractors || 0).toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-xs text-right text-orange-600">${(pnlData.summary?.expense_breakdown?.payroll || 0).toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-xs text-right text-blue-600">${(pnlData.summary?.expense_breakdown?.marketing || 0).toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-xs text-right text-amber-600">${(pnlData.summary?.expense_breakdown?.operating || 0).toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-xs text-right text-gray-900">${((pnlData.summary?.total_expenses || 0) - (pnlData.summary?.expense_breakdown?.owner_draw || 0)).toLocaleString()}</td>
                            <td className="px-2 py-1.5 text-xs text-right text-purple-700">
                              ${((pnlData.summary?.net_profit || 0) + (pnlData.summary?.expense_breakdown?.owner_draw || 0)).toLocaleString()}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-right text-purple-600">
                              {pnlData.summary?.total_revenue > 0 ? Math.round(((pnlData.summary?.net_profit || 0) + (pnlData.summary?.expense_breakdown?.owner_draw || 0)) / pnlData.summary.total_revenue * 100) : 0}%
                            </td>
                            <td className={`px-2 py-1.5 text-xs text-right ${(pnlData.cash_flow?.operating_cash_flow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ${(pnlData.cash_flow?.operating_cash_flow || 0).toLocaleString()}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </Panel>
                )}

                {/* Uncategorized Alert */}
                {pnlData.uncategorized?.length > 0 && (
                  <Panel className="bg-amber-50 border-amber-200 mb-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-amber-800">{pnlData.uncategorized.length} uncategorized transaction(s)</p>
                        <div className="mt-1 space-y-0.5">
                          {pnlData.uncategorized.slice(0, 10).map((tx) => (
                            <p key={tx.id} className="text-xs text-amber-700">
                              {tx.counterparty_name}: ${Math.abs(tx.amount).toLocaleString()} ({new Date(tx.mercury_created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
                            </p>
                          ))}
                        </div>
                        <p className="text-xs text-amber-600 mt-1">Categorize these in Mercury — they'll sync automatically.</p>
                      </div>
                    </div>
                  </Panel>
                )}
              </>
            ) : (
              <Panel className="p-6 text-center text-sm text-gray-500">No P&L data available</Panel>
            )}
          </>
        )}

        {/* TRANSACTIONS TAB */}
        {activeTab === 'transactions' && (<>

        {/* Error */}
        {error && (
          <Panel className="bg-red-50 border-red-200 mb-3">
            <p className="text-xs text-red-800">{error}</p>
          </Panel>
        )}

        <div className="mb-3 flex justify-between items-center">
          <p className="text-xs text-gray-600">{totalCount} transactions for 2026</p>
          <Button onClick={exportCSV} variant="secondary" size="sm">
            <Download className="w-3.5 h-3.5 mr-1" />
            Export CSV
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
          <StatCard
            value={formatCurrency(stats.total)}
            label="Total Revenue"
            icon={DollarSign}
          />
          <StatCard
            value={formatCurrency(stats.net_revenue)}
            label="Net (After Fees)"
            icon={TrendingUp}
          />
          <StatCard
            value={formatCurrency(stats.tips)}
            label="Tips"
            icon={DollarSign}
          />
          <StatCard
            value={formatCurrency(stats.fees)}
            label="Fees Paid"
            icon={CreditCard}
          />
          <StatCard
            value={stats.count}
            label="Transactions"
            icon={Calendar}
          />
        </div>

        {/* Filters */}
        <Panel className="mb-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="col-span-1">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                <input
                  type="text"
                  placeholder="Search by client or ID..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-8 pr-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent"
                />
              </div>
            </div>

            <select
              value={sourceFilter}
              onChange={handleFilterChange(setSourceFilter)}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
            >
              <option value="all">All Sources</option>
              <option value="square">Square</option>
              <option value="cash">Cash</option>
              <option value="venmo">Venmo</option>
              <option value="zelle">Zelle</option>
            </select>

            <select
              value={statusFilter}
              onChange={handleFilterChange(setStatusFilter)}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>

            <button
              onClick={() => { setShowUnmatchedOnly(!showUnmatchedOnly); setCurrentPage(1); }}
              className={`px-2 py-1 text-xs font-medium rounded border transition-colors ${
                showUnmatchedOnly
                  ? 'bg-orange-100 text-orange-800 border-orange-300'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              Unmatched Only
            </button>
          </div>
        </Panel>

        {/* Transactions Table */}
        <Panel noPadding className="overflow-hidden">
          {loading ? (
            <div className="p-6"><LoadingSpinner /></div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('date_time')}
                  >
                    <div className="flex items-center gap-1">
                      Date
                      {sortField === 'date_time' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Client</th>
                  <th
                    className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Amount
                      {sortField === 'amount' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Tip</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Fee</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Net</th>
                  <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Source</th>
                  <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Appt</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Payout</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-2 py-1.5 text-xs text-gray-900 whitespace-nowrap">
                      {formatDate(transaction.date_time)}
                    </td>
                    <td className="px-2 py-1.5">
                      {transaction.client ? (
                        <a
                          href={`/clients/${transaction.client}`}
                          className="flex items-center gap-1 text-red-700 hover:text-red-900 hover:underline"
                        >
                          <User className="w-3 h-3" />
                          <span className="text-xs font-medium">
                            {transaction.client_name || 'Unknown'}
                          </span>
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-right font-medium text-gray-900">
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-right text-green-600">
                      {parseFloat(transaction.tip || 0) > 0 ? formatCurrency(transaction.tip) : '-'}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-right text-red-600">
                      {parseFloat(transaction.fee || 0) > 0 ? formatCurrency(transaction.fee) : '-'}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-right font-medium text-gray-900">
                      {formatCurrency(parseFloat(transaction.amount || 0) - parseFloat(transaction.fee || 0))}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {getSourceIcon(transaction.source)}
                        <span className="text-xs text-gray-600 capitalize">{transaction.source}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <Badge variant={getStatusVariant(transaction.status)} small>
                        {transaction.status}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5">
                      {transaction.appointment_id ? (
                        <a
                          href={`/appointments/${transaction.appointment_id}`}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          #{transaction.appointment_id}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      {transaction.square_id && squarePayoutMap[transaction.square_id] ? (
                        <span className="text-xs text-purple-600 font-mono" title={squarePayoutMap[transaction.square_id]}>
                          {squarePayoutMap[transaction.square_id].slice(-8)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {/* Empty state */}
          {transactions.length === 0 && !loading && (
            <div className="p-8 text-center text-gray-500">
              <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-xs">No transactions found</p>
            </div>
          )}
        </Panel>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalCount)}</span> of{' '}
              <span className="font-medium">{totalCount}</span> results
            </p>
            <div className="flex gap-1">
              <Button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                variant={currentPage === 1 ? 'secondary' : 'primary'}
                size="sm"
              >
                Previous
              </Button>
              {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                const pageNum = currentPage <= 3 ? index + 1 : currentPage - 2 + index;
                if (pageNum > totalPages) return null;
                return (
                  <Button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    variant={currentPage === pageNum ? 'primary' : 'secondary'}
                    size="sm"
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                variant={currentPage === totalPages ? 'secondary' : 'primary'}
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        </>)}
      </div>
    </div>
  );
}

export default FinancePage;
